import { useCallback, useState } from "react";
import { RowClickedEvent } from "ag-grid-community";
import { useHistory } from "react-router-dom";
import { Button, Flex, Grid, Text, TextInput, useDebouncedFn } from "@jsq/ds/core";
import { useAppContext } from "~/contexts/appContext";
import { REPORTING_PAGE_SIZE } from "~/pages/Reporting/constants";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { SPACING } from "~/styles/variables";
import { hasGlobalReportingAccess } from "~/utils/userRoles";
import {
  ClientTemplateRow,
  useFetchClientWorkspaces,
  useClientTemplatesColumnDefs,
} from "./hooks";
import { container, gridContainer } from "./styles_vanilla.css";

const SEARCH_DEBOUNCE_MS = 300;

type QuickSearchInputProps = {
  value?: string;
  onChange: (value: string) => void;
};

export const QuickSearchInput = ({ value = "", onChange }: QuickSearchInputProps) => {
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  return (
    <TextInput
      value={value}
      onChange={handleInputChange}
      placeholder="Search client"
      icon="search"
      isClearable
      onClearValue={() => onChange("")}
      data-cy="quick-search-input"
    />
  );
};

export const ArenaList = () => {
  const history = useHistory();
  const { user } = useAppContext();
  const showSettings = hasGlobalReportingAccess(user.roles);
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const { columnDefs } = useClientTemplatesColumnDefs();
  const { gridOptions } = useAgGridDefaults({
    overrides: {
      paginationPageSize: REPORTING_PAGE_SIZE,
      pagination: true,
    },
  });
  const { rowData } = useFetchClientWorkspaces({
    sortByName: true,
    searchTerm: debouncedSearchTerm,
  });

  const debouncedSetSearchTerm = useDebouncedFn((value: string) => {
    setDebouncedSearchTerm(value);
  }, SEARCH_DEBOUNCE_MS);

  const handleSearchChange = useCallback(
    (value: string) => {
      setInputValue(value);
      debouncedSetSearchTerm(value);
    },
    [debouncedSetSearchTerm],
  );

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<ClientTemplateRow>) => {
      if (event.data?.id && !event.data.isSkeletonRow) {
        history.push(`/reporting/${event.data.id}`);
      }
    },
    [history],
  );

  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM} className={container}>
      <Flex alignItems="center" justifyContent="space-between">
        <Text variant="h2">Custom report templates</Text>
        <Flex gap={8}>
          <QuickSearchInput value={inputValue} onChange={handleSearchChange} />
          {showSettings && (
            <Button
              variant="neutral"
              onClick={() => history.push("/reporting/settings")}
              icon="cog"
            >
              Settings
            </Button>
          )}
        </Flex>
      </Flex>
      <Flex className={gridContainer}>
        <Grid<ClientTemplateRow>
          suppressServerSideFullWidthLoadingRow
          rowData={rowData}
          getRowId={(params) => String(params.data.id)}
          columnDefs={columnDefs}
          gridOptions={gridOptions}
          onRowClicked={handleRowClicked}
        />
      </Flex>
    </Flex>
  );
};
